import { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import { getUser, changePassword, withdraw } from '../services/apiService'

interface UserInfo {
  id: number
  account: string
  username: string
  email: string | null
  phone: string | null
  gender: string | null
  birthDate: string | null
  createdAt: string | null
  officeId: number | null
}

const offices = [
  { id: 1, name: '본사' },
  { id: 2, name: '광주/나주/목포' },
  { id: 3, name: '전주1' },
  { id: 4, name: '부산' },
  { id: 5, name: '세종' },
  { id: 6, name: '대구' },
  { id: 7, name: '구미' },
  { id: 8, name: '청주1' },
  { id: 9, name: '청주2' },
  { id: 10, name: '인천' },
  { id: 11, name: '안산' },
  { id: 12, name: '전주2' },
  { id: 13, name: '익산' }
]

const MyPage = () => {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [pwdForm, setPwdForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  })
  const [pwdErrors, setPwdErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const toast = useToast()
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef(null)
  const [withdrawing, setWithdrawing] = useState(false)

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true)
      try {
        const { data: res } = await getUser()
        if (res.statusCode === 200) {
          setUser(res.data)
        }
      } catch (error: any) {
        if (error.response?.status === 401) {
          toast({
            title: '로그인이 필요합니다.',
            status: 'warning',
            duration: 3000,
            isClosable: true
          })
          navigate('/signIn')
        } else {
          toast({
            title: '오류',
            description: error.response?.data?.message || '정보를 불러오지 못했습니다.',
            status: 'error',
            duration: 3000,
            isClosable: true
          })
        }
      } finally {
        setLoading(false)
      }
    }
    fetchUser()
  }, [navigate, toast])

  const officeName = useMemo(() => {
    if (!user?.officeId) return '-'
    return offices.find((o) => o.id === user.officeId)?.name || `지점 #${user.officeId}`
  }, [user])

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    }

    if (!pwdForm.currentPassword) {
      errors.currentPassword = '현재 비밀번호를 입력하세요.'
    }
    if (!pwdForm.newPassword || pwdForm.newPassword.length < 6) {
      errors.newPassword = '새 비밀번호는 6자 이상이어야 합니다.'
    }
    if (pwdForm.newPassword !== pwdForm.confirmNewPassword) {
      errors.confirmNewPassword = '새 비밀번호가 일치하지 않습니다.'
    }

    setPwdErrors(errors)

    if (errors.currentPassword || errors.newPassword || errors.confirmNewPassword) {
      return
    }

    setSubmitting(true)
    try {
      const { data: res } = await changePassword(pwdForm.currentPassword, pwdForm.newPassword)
      toast({
        title: '비밀번호 변경',
        description: res.message || '비밀번호가 변경되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      setPwdForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      })
    } catch (error: any) {
      toast({
        title: '비밀번호 변경 실패',
        description: error.response?.data?.message || '비밀번호 변경에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleWithdraw = async () => {
    setWithdrawing(true)
    try {
      const { data: res } = await withdraw()
      toast({
        title: '회원 탈퇴 성공',
        description: res.message || '회원 탈퇴가 완료되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      navigate('/signIn')
    } catch (error: any) {
      toast({
        title: '회원 탈퇴 실패',
        description: error.response?.data?.message || '회원 탈퇴 처리에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setWithdrawing(false)
      onClose()
    }
  }

  return (
    <DefaultLayout>
      <Container maxW="container.lg" py={12}>
        <VStack spacing={8} align="stretch">
          <Box>
            <Heading size="lg">마이페이지</Heading>
            <Text color="gray.500" mt={2}>
              회원 정보 확인 및 비밀번호 변경
            </Text>
          </Box>

          {loading ? (
            <Box textAlign="center" py={20}>
              <Spinner size="xl" />
            </Box>
          ) : !user ? (
            <Box textAlign="center" py={20}>
              <Text color="gray.500">회원 정보를 불러올 수 없습니다.</Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box
                p={6}
                bg="white"
                borderRadius="lg"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.100"
              >
                <Heading size="md" mb={4}>
                  회원 정보
                </Heading>
                <VStack align="stretch" spacing={4}>
                  <Stack direction="row" justify="space-between">
                    <Text color="gray.500">아이디</Text>
                    <Text fontWeight="semibold">{user.account}</Text>
                  </Stack>
                  <Stack direction="row" justify="space-between">
                    <Text color="gray.500">이름</Text>
                    <Text fontWeight="semibold">{user.username}</Text>
                  </Stack>
                  <Stack direction="row" justify="space-between">
                    <Text color="gray.500">이메일</Text>
                    <Text fontWeight="semibold">{user.email || '-'}</Text>
                  </Stack>
                  <Stack direction="row" justify="space-between">
                    <Text color="gray.500">휴대폰</Text>
                    <Text fontWeight="semibold">{user.phone || '-'}</Text>
                  </Stack>
                  <Stack direction="row" justify="space-between">
                    <Text color="gray.500">소속</Text>
                    <Text fontWeight="semibold">{officeName}</Text>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justify="space-between">
                    <Text color="gray.500">가입일</Text>
                    <Text fontWeight="semibold">
                      {user.createdAt ? dayjs(user.createdAt).format('YYYY-MM-DD HH:mm') : '-'}
                    </Text>
                  </Stack>
                </VStack>
              </Box>

              <Box
                p={6}
                bg="white"
                borderRadius="lg"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.100"
              >
                <Heading size="md" mb={4}>
                  비밀번호 변경
                </Heading>
                <form onSubmit={handleChangePassword}>
                  <VStack spacing={4} align="stretch">
                    <FormControl isInvalid={!!pwdErrors.currentPassword}>
                      <FormLabel>현재 비밀번호</FormLabel>
                      <Input
                        type="password"
                        value={pwdForm.currentPassword}
                        onChange={(e) =>
                          setPwdForm((prev) => ({ ...prev, currentPassword: e.target.value }))
                        }
                        placeholder="현재 비밀번호를 입력하세요"
                      />
                      <FormErrorMessage>{pwdErrors.currentPassword}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!pwdErrors.newPassword}>
                      <FormLabel>새 비밀번호</FormLabel>
                      <Input
                        type="password"
                        value={pwdForm.newPassword}
                        onChange={(e) =>
                          setPwdForm((prev) => ({ ...prev, newPassword: e.target.value }))
                        }
                        placeholder="새 비밀번호를 입력하세요"
                      />
                      <FormErrorMessage>{pwdErrors.newPassword}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!pwdErrors.confirmNewPassword}>
                      <FormLabel>새 비밀번호 확인</FormLabel>
                      <Input
                        type="password"
                        value={pwdForm.confirmNewPassword}
                        onChange={(e) =>
                          setPwdForm((prev) => ({ ...prev, confirmNewPassword: e.target.value }))
                        }
                        placeholder="새 비밀번호를 다시 입력하세요"
                      />
                      <FormErrorMessage>{pwdErrors.confirmNewPassword}</FormErrorMessage>
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="brand"
                      isLoading={submitting}
                    >
                      비밀번호 변경
                    </Button>
                  </VStack>
                </form>
              </Box>

              <Box
                p={6}
                bg="white"
                borderRadius="lg"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.100"
                gridColumn="1 / -1"
              >
                <Heading size="md" mb={4} color="red.500">
                  회원 탈퇴
                </Heading>
                <Text color="gray.600" mb={4}>
                  회원 탈퇴 시 모든 데이터는 삭제되며 복구할 수 없습니다.<br />
                  정말로 탈퇴하시겠습니까?
                </Text>
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={onOpen}
                >
                  회원 탈퇴하기
                </Button>
              </Box>
            </SimpleGrid>
          )}

          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  회원 탈퇴
                </AlertDialogHeader>

                <AlertDialogBody>
                  정말로 탈퇴하시겠습니까? 이 작업은 되돌릴 수 없습니다.
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={onClose}>
                    취소
                  </Button>
                  <Button colorScheme="red" onClick={handleWithdraw} ml={3} isLoading={withdrawing}>
                    탈퇴하기
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </VStack>
      </Container>
    </DefaultLayout>
  )
}

export default MyPage

